import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { useUserTracking } from "../hooks/custom/useUserTracking";
import UserTrackingTabs from "../components/ui/UserTrackingTabs";
import UserTrackingStats from "../components/ui/UserTrackingStats";
import { RefreshCw, Download } from "lucide-react";

// Import existing components for other tabs
import UserTrackingAnalytics from "../components/ui/UserTrackingAnalytics";
import UserTrackingUsers from "../components/ui/UserTrackingUsers";

const UserTrackingPage: React.FC = () => {
  usePageTitle("User Tracking");
  const navigate = useNavigate();
  const [newUsersDateRange, setNewUsersDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  const {
    // Data
    stats,
    analytics,
    users,
    usersPagination,

    // Loading states
    statsLoading,
    analyticsLoading,
    usersLoading,
    refreshing,

    // Errors
    statsError,
    analyticsError,
    usersError,

    // UI state
    activeTab,
    analyticsDays,
    statsDays,
    currentPage,
    lastUpdated,

    // Handlers
    handleTabChange,
    handleStatsDaysChange,
    handlePageChange,
    handleRefresh,
    handleViewUser,
  } = useUserTracking();

  const formatDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleNewRegistrationsClick = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - statsDays);

    setNewUsersDateRange({
      startDate: formatDateInput(startDate),
      endDate: formatDateInput(endDate),
    });
    handleTabChange("new-users");
  };

  const filteredNewUsers = useMemo(() => {
    const { startDate, endDate } = newUsersDateRange;
    if (!startDate && !endDate) return users;

    const start = startDate ? new Date(startDate) : null;
    if (start) start.setHours(0, 0, 0, 0);
    const end = endDate ? new Date(endDate) : null;
    if (end) end.setHours(23, 59, 59, 999);

    return users.filter((user) => {
      const createdAt = user?.accountCreatedDate || user?.createdAt;
      if (!createdAt) return false;
      const createdDate = new Date(createdAt);
      if (Number.isNaN(createdDate.getTime())) return false;
      if (start && createdDate < start) return false;
      if (end && createdDate > end) return false;
      return true;
    });
  }, [newUsersDateRange, users]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "stats":
        return (
          <div>
          <UserTrackingStats
            stats={stats}
            isLoading={statsLoading}
            selectedDays={statsDays}
            onDaysChange={handleStatsDaysChange}
            onNewRegistrationsClick={handleNewRegistrationsClick}
            onAvgSessionClick={() => navigate("/user-tracking/usage")}
          />

            {statsError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="text-red-800 font-medium">
                  Error Loading Stats
                </h3>
                <p className="text-red-600 mt-1">{statsError}</p>
              </div>
            )}
          </div>
        );

      case "analytics":
        return (
          <UserTrackingAnalytics
            analytics={analytics}
            isLoading={analyticsLoading}
            error={analyticsError}
            days={analyticsDays}
          />
        );

      case "users":
        return (
          <UserTrackingUsers
            users={users}
            isLoading={usersLoading}
            error={usersError}
            pagination={usersPagination}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onViewUser={handleViewUser}
          />
        );
      case "new-users":
        return (
          <div>
            <div className="mb-4 text-sm text-gray-600">
              Showing newest registrations, sorted by account creation date.
            </div>
            <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newUsersDateRange.startDate}
                    onChange={(e) =>
                      setNewUsersDateRange({
                        ...newUsersDateRange,
                        startDate: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newUsersDateRange.endDate}
                    onChange={(e) =>
                      setNewUsersDateRange({
                        ...newUsersDateRange,
                        endDate: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <button
                  onClick={() =>
                    setNewUsersDateRange({ startDate: "", endDate: "" })
                  }
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
            <UserTrackingUsers
              users={filteredNewUsers}
              isLoading={usersLoading}
              error={usersError}
              pagination={usersPagination}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onViewUser={handleViewUser}
              showCreatedDate
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Tracking</h1>
          <p className="text-gray-600 mt-1">
            Monitor user activity and behavior analytics
          </p>
          {lastUpdated && (
            <p className="text-sm text-gray-500 mt-1">
              Last updated: {lastUpdated.toLocaleString()}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh data"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>

          <button
            onClick={() => {}}
            className="flex items-center gap-2 px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            title="Export data"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <UserTrackingTabs activeTab={activeTab} setActiveTab={handleTabChange} />

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default UserTrackingPage;
